"use client";

import { ArrowUpRight, BadgeCheck, Plus } from "lucide-react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useSpring,
} from "motion/react";
import { useTranslations } from "next-intl";
import {
  type FocusEvent,
  type PointerEvent,
  useCallback,
  useRef,
  useState,
} from "react";

import { EASE_OUT_EXPO } from "@/components/motion/reveal";
import { clamp, cn } from "@/lib/utils";

import { useCanHover } from "../_utils/use-can-hover";

import { FloatingCard } from "./floating-card";
import { TrainingDetails, type TrainingItem } from "./training-details";
import { RevealRow, RowMeta, RowSeparator, RowTitle } from "./training-row";

// =============================================================================

/** Gap between the pointer and the card, in px */
const CARD_OFFSET = 36;
/** Minimal distance to the edges of the viewport (under the navbar on top) */
const EDGE = 16;
const TOP_EDGE = 88;
const SPRING = { stiffness: 170, damping: 24, mass: 0.55 };

type TrainingListProps = { items: TrainingItem[] };

/**
 * Numbered list of the trainings. Mouse on a wide screen: the hovered row
 * lights up, the others fade and a card with the details follows the
 * pointer. Touch or narrow screens: an accordion with the same details.
 */
export function TrainingList({ items }: TrainingListProps) {
  const canHover = useCanHover();
  return canHover ? (
    <HoverList items={items} />
  ) : (
    <AccordionList items={items} />
  );
}

// =============================================================================
// Mouse on a wide screen: hover rows + floating card
// =============================================================================

function HoverList({ items }: TrainingListProps) {
  const t = useTranslations("Trainings");
  const tCommon = useTranslations("Common");
  const shouldReduceMotion = useReducedMotion();
  const [activeId, setActiveId] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const hasPosition = useRef(false);
  // Right edge of the hovered title: the card stays clear of it
  const titleRight = useRef<number | null>(null);
  const x = useSpring(0, SPRING);
  const y = useSpring(0, SPRING);

  const activeItem = items.find((item) => item.id === activeId) ?? null;

  // The card may not be mounted yet: fall back to its CSS size
  const cardSize = useCallback(() => {
    const fallback = clamp(window.innerWidth * 0.314, 320, 452);
    return {
      width: cardRef.current?.offsetWidth ?? fallback,
      height: cardRef.current?.offsetHeight ?? fallback,
    };
  }, []);

  const moveCard = useCallback(
    (left: number, top: number) => {
      const { width, height } = cardSize();
      const nextX = clamp(left, EDGE, window.innerWidth - width - EDGE);
      const nextY = clamp(top, TOP_EDGE, window.innerHeight - height - EDGE);
      // First position: no flight from the corner of the screen
      if (!hasPosition.current || shouldReduceMotion) {
        x.jump(nextX);
        y.jump(nextY);
        hasPosition.current = true;
      } else {
        x.set(nextX);
        y.set(nextY);
      }
    },
    [cardSize, shouldReduceMotion, x, y]
  );

  const onPointerMove = (event: PointerEvent) => {
    if (event.pointerType !== "mouse") return;
    const { width, height } = cardSize();
    const maxLeft = window.innerWidth - width - EDGE;
    // Right of the pointer, never over the hovered title when there is room
    const titleEnd = titleRight.current ?? -Infinity;
    let left = Math.max(event.clientX + CARD_OFFSET, titleEnd + CARD_OFFSET);
    if (left > maxLeft) {
      left =
        event.clientX > titleEnd
          ? event.clientX - width - CARD_OFFSET
          : maxLeft;
    }
    moveCard(left, event.clientY - height * 0.62);
  };

  const onRowEnter = (event: PointerEvent<HTMLElement>, id: string) => {
    if (event.pointerType !== "mouse") return;
    const title = event.currentTarget.querySelector("[data-row-title]");
    titleRight.current = title?.getBoundingClientRect().right ?? null;
    setActiveId(id);
  };

  const onPointerLeave = () => {
    setActiveId(null);
    titleRight.current = null;
    hasPosition.current = false;
  };

  // Keyboard: the card shows up at the right end of the focused row
  const onRowFocus = (event: FocusEvent<HTMLElement>, id: string) => {
    if (!(event.target as HTMLElement).matches(":focus-visible")) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const { width, height } = cardSize();
    hasPosition.current = false;
    moveCard(
      rect.right - width - CARD_OFFSET * 3,
      rect.top + rect.height / 2 - height / 2
    );
    setActiveId(id);
  };

  const onRowBlur = (event: FocusEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }
    setActiveId(null);
    hasPosition.current = false;
  };

  return (
    <div onPointerMove={onPointerMove} onPointerLeave={onPointerLeave}>
      <ul className="relative">
        <RevealRow aria-hidden className="relative block h-px">
          <RowSeparator position="top" />
        </RevealRow>
        {items.map((item) => {
          const isActive = item.id === activeId;
          const isDimmed = activeId !== null && !isActive;
          return (
            <RevealRow
              key={item.id}
              tabIndex={0}
              onPointerEnter={(event) => onRowEnter(event, item.id)}
              onFocus={(event) => onRowFocus(event, item.id)}
              onBlur={onRowBlur}
              className="relative flex items-center gap-8 py-[clamp(2rem,5.6vw,5rem)] outline-offset-0"
            >
              {/* Lit row */}
              <span
                aria-hidden
                className={cn(
                  "pointer-events-none absolute inset-0 bg-linear-to-r from-foreground/3 via-foreground/1 to-transparent opacity-0 transition-opacity duration-500 ease-out-quart",
                  isActive && "opacity-100"
                )}
              />
              <h2 className="relative min-w-0 flex-1">
                <RowTitle item={item} isActive={isActive} isDimmed={isDimmed} />
              </h2>
              <RowMeta
                className="relative"
                typeLabel={t(`types.${item.type}`)}
                isActive={isActive}
                isDimmed={isDimmed}
                verify={
                  item.credentialUrl
                    ? {
                        href: item.credentialUrl,
                        label: t("verify"),
                        ariaLabel: t("verifyLabel", { title: item.title }),
                        newTab: t("newTab"),
                      }
                    : undefined
                }
              />
              {/* Same details as the card, for assistive technologies */}
              <div className="sr-only">
                <TrainingDetails
                  item={item}
                  typeLabel={t(`types.${item.type}`)}
                  inProgressLabel={tCommon("inProgress")}
                  variant="panel"
                />
              </div>
              <RowSeparator position="bottom" />
            </RevealRow>
          );
        })}
      </ul>

      <FloatingCard item={activeItem} x={x} y={y} cardRef={cardRef} />
    </div>
  );
}

// =============================================================================
// Touch or narrow screen: accordion
// =============================================================================

function AccordionList({ items }: TrainingListProps) {
  const t = useTranslations("Trainings");
  const tCommon = useTranslations("Common");
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <ul className="relative">
      <RevealRow aria-hidden className="relative block h-px">
        <RowSeparator position="top" />
      </RevealRow>
      {items.map((item) => {
        const isOpen = item.id === openId;
        const buttonId = `training-${item.id}`;
        const panelId = `training-panel-${item.id}`;
        return (
          <RevealRow key={item.id} className="relative">
            <h2>
              <button
                id={buttonId}
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenId(isOpen ? null : item.id)}
                className="flex w-full items-center gap-5 py-[clamp(1.75rem,5vw,2.75rem)] text-left"
              >
                <RowTitle item={item} />
                <span
                  aria-hidden
                  className={cn(
                    "grid size-10 shrink-0 place-items-center rounded-full border border-line transition-transform duration-500 ease-out-expo",
                    isOpen && "rotate-45"
                  )}
                >
                  <Plus className="size-4" />
                </span>
              </button>
            </h2>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.55, ease: EASE_OUT_EXPO }}
                  className="overflow-hidden"
                >
                  <div className="pb-8 sm:pl-[clamp(2.5rem,6vw,4.5rem)]">
                    <TrainingDetails
                      item={item}
                      typeLabel={t(`types.${item.type}`)}
                      inProgressLabel={tCommon("inProgress")}
                      variant="panel"
                    />
                    {item.credentialUrl && (
                      <a
                        href={item.credentialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${t("verifyLabel", { title: item.title })} ${t("newTab")}`}
                        className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-sm"
                      >
                        <BadgeCheck
                          className="size-4 text-brand"
                          strokeWidth={1.75}
                        />
                        {t("verify")}
                        <ArrowUpRight className="size-3.5" />
                      </a>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <RowSeparator position="bottom" />
          </RevealRow>
        );
      })}
    </ul>
  );
}

// =============================================================================
